import Medusa from '@medusajs/medusa-js';
import { QueryClient } from '@tanstack/react-query';
import { verboseLog, verboseError, isVerboseLoggingEnabled } from '@lib/util/logging-util';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 60 * 24,
            retry: 1,
        },
    },
});

// Cache of Medusa client instances by baseUrl
const medusaClientInstances = new Map<string, Medusa>();

/**
 * Get or create the Medusa client instance
 * This allows the client to be initialized with runtime config
 * Creates separate instances for different backend URLs
 *
 * In client components, use: useApi().getMedusaClient()
 * In server components, pass the apiKey and backendUrl from getServerRuntimeConfig()
 */
export function getMedusaClient(apiKey?: string, backendUrl?: string): Medusa {
    const requestId = `medusa-client-${Date.now()}`;
    const isVerbose = isVerboseLoggingEnabled();

    if (isVerbose) {
        verboseLog(`[getMedusaClient][${requestId}] ========== START ==========`);
        verboseLog(`[getMedusaClient][${requestId}] Input parameters:`);
        verboseLog(`[getMedusaClient][${requestId}]   backendUrl param: ${backendUrl || '(NOT PROVIDED)'}`);
        verboseLog(`[getMedusaClient][${requestId}]   apiKey param: ${apiKey ? '***SET*** (length: ' + apiKey.length + ')' : '(NOT PROVIDED)'}`);
        verboseLog(`[getMedusaClient][${requestId}] Environment variables:`);
        verboseLog(`[getMedusaClient][${requestId}]   process.env.MEDUSA_BACKEND_URL: ${process.env.MEDUSA_BACKEND_URL || '(NOT SET)'}`);
        verboseLog(`[getMedusaClient][${requestId}]   process.env.MEDUSA_BACKEND_URL_CLIENT: ${process.env.MEDUSA_BACKEND_URL_CLIENT || '(NOT SET)'}`);
        verboseLog(`[getMedusaClient][${requestId}]   process.env.CLIENT_API_KEY: ${process.env.CLIENT_API_KEY ? '***SET***' : '(NOT SET)'}`);
    }

    const medusaBackendUrl = backendUrl || process.env.MEDUSA_BACKEND_URL_CLIENT || process.env.MEDUSA_BACKEND_URL || process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || '';
    const clientApiKey = apiKey || process.env.CLIENT_API_KEY || process.env.NEXT_PUBLIC_CLIENT_API_KEY || '';

    if (isVerbose) {
        verboseLog(`[getMedusaClient][${requestId}] Resolved values:`);
        verboseLog(`[getMedusaClient][${requestId}]   Final medusaBackendUrl: ${medusaBackendUrl || '(EMPTY STRING!)'}`);
        verboseLog(`[getMedusaClient][${requestId}]   Final clientApiKey: ${clientApiKey ? '***SET*** (length: ' + clientApiKey.length + ')' : '(EMPTY STRING!)'}`);
    }

    // Validate medusaBackendUrl (always log errors)
    if (!medusaBackendUrl) {
        verboseError(`[getMedusaClient][${requestId}] ========== CONFIGURATION ERROR ==========`);
        verboseError(`[getMedusaClient][${requestId}] MEDUSA_BACKEND_URL is empty!`);
        verboseError(`[getMedusaClient][${requestId}] The Medusa client will use the current page origin as the base URL.`);
        verboseError(`[getMedusaClient][${requestId}] This will cause requests to go to the storefront instead of Medusa!`);
    } else if (!medusaBackendUrl.startsWith('http://') && !medusaBackendUrl.startsWith('https://')) {
        verboseError(`[getMedusaClient][${requestId}] ========== CONFIGURATION ERROR ==========`);
        verboseError(`[getMedusaClient][${requestId}] MEDUSA_BACKEND_URL is not an absolute URL: ${medusaBackendUrl}`);
        verboseError(`[getMedusaClient][${requestId}] It must start with http:// or https://`);
        verboseError(`[getMedusaClient][${requestId}] The Medusa client will resolve this relative to the current page origin!`);
    }

    // Use baseUrl as cache key to support multiple backend URLs
    const cacheKey = `${medusaBackendUrl}|${clientApiKey}`;

    if (!medusaClientInstances.has(cacheKey)) {
        if (isVerbose) {
            verboseLog(`[getMedusaClient][${requestId}] Creating new Medusa client instance`);
            verboseLog(`[getMedusaClient][${requestId}]   baseUrl: ${medusaBackendUrl}`);
            verboseLog(`[getMedusaClient][${requestId}]   x-api-key: ${clientApiKey ? '***MASKED***' : '(NOT SET)'}`);
        }
        medusaClientInstances.set(cacheKey, new Medusa({
            baseUrl: medusaBackendUrl,
            maxRetries: 3,
            customHeaders: {
                'x-api-key': clientApiKey,
            },
        }));
    } else if (isVerbose) {
        verboseLog(`[getMedusaClient][${requestId}] Using cached Medusa client instance`);
    }

    if (isVerbose) {
        verboseLog(`[getMedusaClient][${requestId}] ========== END ==========`);
    }
    return medusaClientInstances.get(cacheKey)!;
}

/**
 * @deprecated Legacy medusaClient export. Use useApi().getMedusaClient() in client components
 * or getMedusaClient(apiKey, backendUrl) with getServerRuntimeConfig() in server components.
 *
 * This creates a client at module load time which will log warnings in the browser
 * because process.env is not available client-side. This is expected for legacy code.
 */
const medusaClient = (() => {
  // Only create the client on the server to avoid console errors
  if (typeof window === 'undefined') {
    return getMedusaClient();
  }
  // Return a placeholder for client-side - components should use useApi() hook
  return null as any;
})();

/**
 * @deprecated Import these from getServerRuntimeConfig() in server components
 * or use useApi() hook in client components instead
 *
 * These constants are kept for backward compatibility with existing code that hasn't
 * been migrated to runtime config yet. They will read from process.env at runtime.
 *
 * NOTE: NEXT_PUBLIC_* variables are NOT used here because they are baked into the
 * client bundle at build time with build machine values, not runtime values.
 */
const MEDUSA_BACKEND_URL = process.env.MEDUSA_BACKEND_URL || '';
const HOSTING_ENV = process.env.HOST_ENV || process.env.SENTRY_ENV || '';
const SEARCH_SERVICE_URL = process.env.SEARCH_SERVICE_URL || '';
const SEARCH_SERVICE_TOKEN = process.env.SEARCH_SERVICE_TOKEN || '';
const IFRAME_SERVICE_URL = process.env.IFRAME_SERVICE_URL || '';
const REVALIDATE_WINDOW = 60 * 30; // 30 minutes
const BASE_URL = process.env.BASE_URL || '';
const REDEMPTION_SERVICE_TOKEN = process.env.REDEMPTION_SERVICE_TOKEN || '';
const REDEMPTION_SERVICE_URL = process.env.REDEMPTION_SERVICE_URL || '';
const CLIENT_API_KEY = process.env.CLIENT_API_KEY || '';

export {
    MEDUSA_BACKEND_URL,
    queryClient,
    medusaClient,
    SEARCH_SERVICE_URL,
    SEARCH_SERVICE_TOKEN,
    IFRAME_SERVICE_URL,
    REVALIDATE_WINDOW,
    HOSTING_ENV,
    BASE_URL,
    REDEMPTION_SERVICE_TOKEN,
    REDEMPTION_SERVICE_URL,
    CLIENT_API_KEY
};
